import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Typography, Tabs, Spinner, Alert, Switch } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type VendorSettings } from '../lib/adminApiClient';
import { LocationsTab } from './vendorReview/LocationsTab';
import { QuestionsTab } from './vendorReview/QuestionsTab';
import { ChangeRequestsTab } from './vendorReview/ChangeRequestsTab';
import { CircularsTab } from './vendorReview/CircularsTab';
import { CertificationsTab } from './vendorReview/CertificationsTab';
import { OwnershipTab } from './vendorReview/OwnershipTab';
import { MUTED } from './vendorReview/parts';

/**
 * The vendor review queue (§15u): one place for everything a vendor sends that
 * a person must look at before shoppers see it — a location, a product nobody
 * could identify, a change to locked product information.
 */

type Tab = 'locations' | 'products' | 'changes' | 'circulars' | 'certifications' | 'ownership';
const TABS: Tab[] = ['locations', 'products', 'changes', 'circulars', 'certifications', 'ownership'];

export default function VendorReview() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canReview = caps.has('vendor:review');
  const canManage = caps.has('vendor:manage');
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'products';
  const [counts, setCounts] = useState<Record<Tab, number | undefined>>({ locations: undefined, products: undefined, changes: undefined, circulars: undefined, certifications: undefined, ownership: undefined });
  const onLocations = useCallback((n: number) => setCounts((c) => ({ ...c, locations: n })), []);
  const onProducts = useCallback((n: number) => setCounts((c) => ({ ...c, products: n })), []);
  const onChanges = useCallback((n: number) => setCounts((c) => ({ ...c, changes: n })), []);
  const onCirculars = useCallback((n: number) => setCounts((c) => ({ ...c, circulars: n })), []);
  const onCertifications = useCallback((n: number) => setCounts((c) => ({ ...c, certifications: n })), []);
  const onOwnership = useCallback((n: number) => setCounts((c) => ({ ...c, ownership: n })), []);

  if (caps.loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view the vendor review queue.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Vendor review
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        What vendors have sent that a person checks before shoppers see it.
      </Typography>
      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can read the queue but not decide.</Alert>
        </Box>
      )}
      {canManage && <ImageGenerationSwitch />}
      <Box sx={{ mb: 2 }}>
        <Tabs
          ariaLabel="Vendor review queue"
          value={tab}
          onChange={(_e, v) => setParams({ tab: v })}
          tabs={[
            { value: 'products', label: 'Products to identify', badgeCount: counts.products },
            { value: 'changes', label: 'Change requests', badgeCount: counts.changes },
            { value: 'locations', label: 'Locations', badgeCount: counts.locations },
            { value: 'circulars', label: 'Circulars & images', badgeCount: counts.circulars },
            { value: 'certifications', label: 'Certifications', badgeCount: counts.certifications },
            { value: 'ownership', label: 'Ownership', badgeCount: counts.ownership },
          ]}
        />
      </Box>
      {tab === 'locations' && <LocationsTab canReview={canReview} onCount={onLocations} />}
      {tab === 'products' && <QuestionsTab canReview={canReview} onCount={onProducts} />}
      {tab === 'changes' && <ChangeRequestsTab canReview={canReview} onCount={onChanges} />}
      {tab === 'circulars' && <CircularsTab canReview={canReview} onCount={onCirculars} />}
      {tab === 'certifications' && <CertificationsTab canReview={canReview} onCount={onCertifications} />}
      {tab === 'ownership' && <OwnershipTab canReview={canReview} onCount={onOwnership} />}
    </Box>
  );
}

function ImageGenerationSwitch() {
  const [settings, setSettings] = useState<VendorSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApiClient
      .getVendorSettings()
      .then(setSettings)
      .catch((err: Error) => setError(err.message));
  }, []);

  const toggle = async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setSettings(await adminApiClient.setVendorImageGeneration(enabled));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };

  const enabled = settings?.imageGeneration.enabled ?? false;
  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }} data-testid="vendor-image-generation">
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Switch
          ariaLabel="Make photos for vendor products that have none"
          label={`Make photos for vendor products that have none — ${enabled ? 'on' : 'off'}`}
          labelPlacement="end"
          labelStyle={{ fontSize: 15, fontWeight: 700 }}
          checked={enabled}
          disabled={!settings || busy}
          onChange={(_e, v) => void toggle(v)}
        />
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {enabled ? 'A product a vendor sends without a photo gets one we make.' : 'A product a vendor sends without a photo shows the standard placeholder.'}
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
